"use client"
import { Badge } from '@radix-ui/themes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRBAC } from './RBACGuard';

import {
  AlertCircle,
  AlertTriangle,
  Clock,
  FileText,
  HelpCircle,
  Info,
  RefreshCw,
  Shield,
  Users,
  Wifi,
  WifiOff
} from 'lucide-react';

interface StatData {
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
}

interface StatsResponse {
  totalBlogs: StatData;
  totalNews: StatData;
  totalPR: StatData;
  totalThoughtLeadership: StatData;
  totalFAQs: StatData;
  totalTestimonials: StatData;
  pageViewsBlogs: StatData;
  pageViewsNews: StatData;
  pageViewsPR: StatData;
  pageViewsThoughtLeadership: StatData;
}

const statConfig = [
  {
    key: 'totalBlogs' as keyof StatsResponse,
    name: 'Total Blogs',
    icon: FileText,
    color: 'bg-blue-500',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    borderColor: 'bg-blue-500'
  },
  {
    key: 'totalNews' as keyof StatsResponse,
    name: 'Total News',
    icon: FileText,
    color: 'bg-green-500',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    borderColor: 'bg-emerald-500'
  },
  {
    key: 'totalPR' as keyof StatsResponse,
    name: 'Total PR',
    icon: FileText,
    color: 'bg-purple-500',
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    borderColor: 'bg-purple-500'
  },
  {
    key: 'totalThoughtLeadership' as keyof StatsResponse,
    name: 'Total Thought Leadership',
    icon: FileText,
    color: 'bg-indigo-500',
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    borderColor: 'bg-indigo-500'
  },
  {
    key: 'totalFAQs' as keyof StatsResponse,
    name: 'Total FAQs',
    icon: HelpCircle,
    color: 'bg-yellow-500',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    borderColor: 'bg-amber-500'
  },
  {
    key: 'totalTestimonials' as keyof StatsResponse,
    name: 'Total Testimonials',
    icon: Users,
    color: 'bg-pink-500',
    iconBg: 'bg-pink-50',
    iconColor: 'text-pink-600',
    borderColor: 'bg-pink-500'
  }
];

const pageViewsConfig = [
  {
    key: 'pageViewsBlogs' as keyof StatsResponse,
    name: 'Blogs',
    color: 'bg-blue-600'
  },
  {
    key: 'pageViewsNews' as keyof StatsResponse,
    name: 'News',
    color: 'bg-green-600'
  },
  {
    key: 'pageViewsPR' as keyof StatsResponse,
    name: 'PR',
    color: 'bg-purple-600'
  },
  {
    key: 'pageViewsThoughtLeadership' as keyof StatsResponse,
    name: 'Thought Leadership',
    color: 'bg-indigo-600'
  }
];

type ErrorType = 'network' | 'auth' | 'server' | 'timeout' | 'unknown';

interface ErrorState {
  type: ErrorType;
  message: string;
  details?: string;
  canRetry: boolean;
}

export default function DashboardStats() {
  const { isAdmin, user, role } = useRBAC();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorState | null>(null);
  const [isOnline, setIsOnline] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [_lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [_selfLoading, setSelfLoading] = useState(false);
  const [_selfStats, setSelfStats] = useState<StatsResponse | null>(null);
  const errorTypeRef = useRef<ErrorType | null>(null);

  useEffect(() => {
    setIsOnline(navigator.onLine);
  }, []);

  // Stabilize role checker to avoid re-creating callbacks that depend on it
  const isAdminRef = useRef(isAdmin);
  useEffect(() => { isAdminRef.current = isAdmin; }, [isAdmin]);
  useEffect(() => {
    errorTypeRef.current = error?.type ?? null;
  }, [error?.type]);

  const fetchStats = useCallback(async () => {
    const getErrorState = (error: unknown, response?: Response): ErrorState => {
      if (!navigator.onLine) {
        return {
          type: 'network',
          message: 'No internet connection',
          details: 'Please check your network connection and try again.',
          canRetry: true
        };
      }

      if (response && (response.status === 401 || response.status === 403)) {
        return {
          type: 'auth',
          message: 'Authentication required',
          details: 'Your session has expired. Please log in again.',
          canRetry: false
        };
      }

      if (response && response.status >= 500) {
        return {
          type: 'server',
          message: 'Server error',
          details: 'The server is currently experiencing issues. Please try again later.',
          canRetry: true
        };
      }

      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('timeout'))) {
        return {
          type: 'timeout',
          message: 'Request timeout',
          details: 'The request took too long to complete. Please try again.',
          canRetry: true
        };
      }

      return {
        type: 'unknown',
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        details: 'Something went wrong while loading the statistics.',
        canRetry: true
      };
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/admin/dashboard-stats', {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (data.success && data.stats) {
        setStats(data.stats);
        setLastUpdated(new Date());
        setRetryCount(0);
      } else {
        throw new Error('Invalid response format from server');
      }

      // If not admin, also load self-scoped stats
      if (!isAdminRef.current()) {
        try {
          setSelfLoading(true);
          const selfResp = await fetch('/api/admin/dashboard-stats?scope=self', {
            headers: { 'Cache-Control': 'no-cache' }
          });
          const selfData = await selfResp.json();
          if (selfResp.ok && selfData.success && selfData.stats) {
            setSelfStats(selfData.stats);
          } else if (!selfResp.ok) {
            throw new Error(selfData.error || `HTTP ${selfResp.status}`);
          }
        } catch (e) {
          // Non-fatal for page; just omit personal stats
          console.warn('Failed to load self stats', e);
        } finally {
          setSelfLoading(false);
        }
      } else {
        setSelfStats(null);
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      const errorState = getErrorState(err, err instanceof Response ? err : undefined);
      setError(errorState);
    } finally {
      setLoading(false);
      clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    // Network status monitoring
    const handleOnline = () => {
      setIsOnline(true);
      if (errorTypeRef.current === 'network') {
        fetchStats(); // Auto retry when connection is restored
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setError({
        type: 'network',
        message: 'Connection lost',
        details: 'You appear to be offline. Statistics will update when connection is restored.',
        canRetry: false
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchStats]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchStats();
  };

  const getErrorIcon = (errorType: ErrorType) => {
    switch (errorType) {
      case 'network':
        return <WifiOff className="h-5 w-5 text-red-500" />;
      case 'auth':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'server':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'timeout':
        return <Clock className="h-5 w-5 text-blue-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getErrorColor = (errorType: ErrorType) => {
    switch (errorType) {
      case 'network':
        return 'border-red-200 bg-red-50';
      case 'auth':
        return 'border-yellow-200 bg-yellow-50';
      case 'server':
        return 'border-orange-200 bg-orange-50';
      case 'timeout':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-red-200 bg-red-50';
    }
  };

  const visibleStatConfig = statConfig;

  if (loading) {
    return (
      <div className="space-y-4">
        {/* Loading Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-sm  border border-gray-200 p-1 shadow-sm h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
            <span className="text-sm text-gray-600">Loading statistics...</span>
          </div>
          <div className="flex items-center space-x-2">
            <Wifi className={`h-4 w-4 ${isOnline ? 'text-green-500' : 'text-red-500'}`} />
            <span className="text-xs text-gray-500">
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        {/* Loading Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleStatConfig.map((config) => (
            <div
              key={config.key}
              className="bg-white rounded-lg shadow-sm border border-gray-200 2xl:p-6 p-4"
            >
              <div className="flex items-center justify-between">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                </div>
                <div className={` rounded-md  border border-gray-200 p-2 shadow-sm ${config.color} opacity-50`}>
                  <config.icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="mt-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {/* Error Header */}
        <div className={`rounded-lg border p-4 ${getErrorColor(error.type)}`}>
          <div className="flex items-start space-x-3">
            {getErrorIcon(error.type)}
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">{error.message}</h3>
              {error.details && (
                <p className="text-sm text-gray-700 mt-1">{error.details}</p>
              )}
              {retryCount > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  Attempt {retryCount + 1} of 3
                </p>
              )}
            </div>
            {error.canRetry && (
              <button
                onClick={handleRetry}
                disabled={retryCount >= 3}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {retryCount >= 3 ? 'Max retries reached' : 'Try Again'}
              </button>
            )}
          </div>
        </div>

        {/* Error Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleStatConfig.map((config) => (
            <div
              key={config.key}
              className="bg-white rounded-lg shadow-sm border border-gray-200 2xl:p-6 p-4"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-0">{config.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2 mb-0">--</p>
                </div>
                <div className={` rounded-md  border border-gray-200 p-2 shadow-sm ${config.color} opacity-50`}>
                  <config.icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center text-gray-500">
                  <Info className="h-4 w-4 mr-1" />
                  <span className="text-sm">Data unavailable</span>
                </div>
                {!isOnline && (
                  <div className="flex items-center text-orange-600">
                    <WifiOff className="h-4 w-4 mr-1" />
                    <span className="text-xs">Offline</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default stats with zero values when no data is available
  const defaultStats: StatsResponse = {
    totalBlogs: { value: '0', change: '0', changeType: 'positive' },
    totalNews: { value: '0', change: '0', changeType: 'positive' },
    totalPR: { value: '0', change: '0', changeType: 'positive' },
    totalThoughtLeadership: { value: '0', change: '0', changeType: 'positive' },
    totalFAQs: { value: '0', change: '0', changeType: 'positive' },
    totalTestimonials: { value: '0', change: '0', changeType: 'positive' },
    pageViewsBlogs: { value: '0', change: '0', changeType: 'positive' },
    pageViewsNews: { value: '0', change: '0', changeType: 'positive' },
    pageViewsPR: { value: '0', change: '0', changeType: 'positive' },
    pageViewsThoughtLeadership: { value: '0', change: '0', changeType: 'positive' }
  };

  const displayStats = stats || defaultStats;

  return (
    <div className="space-y-6">
      {/* Welcome & Statistics Section */}
      <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
        {/* Welcome Header */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 border-b border-gray-100 px-6 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="h-1 w-1 rounded-full bg-blue-500 animate-pulse"></div>
                <h2 className="text-xl font-medium text-gray-900 tracking-tight">
                  Welcome back, <span className="text-blue-600">{user?.name || 'User'}</span>!
                </h2>
              </div>
              <p className="text-gray-600 text-sm ml-4 mt-1">
                Here&apos;s what&apos;s happening with your content today.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Badge
                variant="soft"
                color={
                  role === 'Admin' ? 'red' :
                    role === 'Editor' ? 'blue' : 'green'
                }
                className="!px-6 !py-2.5 !rounded-md shadow-sm"
              >
                <Shield className="h-4 w-4 mr-1.5" />
                {role}
              </Badge>
            </div>
          </div>
        </div>

        {/* Statistics Content - glassmorphic theme to match Blog Management */}
        <div className="p-6 bg-gradient-to-b from-white to-gray-50/40">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {visibleStatConfig.map((config) => {
              const statData = displayStats[config.key];
              const Icon = config.icon;
            

              return (
                <div
                  key={config.key}
                  className="group rounded-md border border-gray-200 p-4 hover:shadow-lg hover:border-gray-300 transition-all duration-300 relative overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                >
                  {/* Left border - 2/3 height, centered */}
                  <div
                    className={`absolute left-0 top-1/2 -translate-y-1/2 ${config.borderColor}`}
                    style={{
                      width: '2px',
                      height: '66.67%',
                      borderRadius: '0 2px 2px 0'
                    }}
                  />

                  {/* Subtle gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          {config.name}
                        </p>
                        <p className="text-2xl font-bold text-gray-900">{statData.value}</p>
                      </div>
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-sm ${config.iconBg} ${config.iconColor} shadow-sm transition-transform group-hover:scale-105 border border-gray-200 p-1`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                    </div>
                   
                  </div>
                </div>
              );
            })}

            {/* Combined Page Views Card - Last 2 columns */}
            <div className="group bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-gray-300 transition-all duration-300 md:col-span-2 lg:col-span-2 relative overflow-hidden">
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative z-10">
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Page Views</p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {pageViewsConfig.map((config) => {
                    const statData = displayStats[config.key];

                    return (
                      <div key={config.key} className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-3 border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200 group/item">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-600">{config.name}</p>
                          <div className={`h-1.5 w-1.5 rounded-full ${config.color}`}></div>
                        </div>
                        <p className="text-xl font-bold text-gray-900">{statData.value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
